/**
 * BeneficiosPage — "Beneficios Cantabria".
 *
 * Tablero del portal de promociones (juegos, participaciones y premios),
 * alimentado por /api/beneficios/overview, que a su vez consume la API de BI
 * cantabri_api_bi. El token Bearer vive en el backend; aquí no se toca.
 *
 * LO QUE CONDICIONA EL DISEÑO (ver doc_beneficios/HALLAZGOS_API_BI.md):
 *
 *  - La API NO acepta filtros de fecha en ningún endpoint. Los indicadores de
 *    arriba son acumulados de TODO el periodo y no se pueden recortar. Por eso
 *    el selector de rango vive pegado al bloque histórico y no en la cabecera:
 *    si estuviera arriba, se leería como si filtrara los KPIs, que es mentira.
 *    El aviso amarillo lo dice explícito; es el malentendido más caro de esta
 *    pestaña.
 *
 *  - No existe endpoint de "registros por mes". Lo único histórico son las
 *    series por día, así que las vistas semanal y mensual se agrupan AQUÍ, en
 *    el cliente. La semana usa ISO-8601 (lunes a domingo) para coincidir con
 *    getRegistrationsByWeek.
 *
 *  - award_rate es premios / PARTICIPACIONES, no premios / usuarios. El embudo
 *    está partido en dos bloques (usuarios arriba, participaciones abajo) justo
 *    para no mezclar unidades.
 *
 *  - El filtro por juego NO filtra el tablero entero: la API solo desglosa por
 *    juego interacciones, usuarios únicos y tiempos (mín/prom/máx). Registros,
 *    participación, premios, series e horarios son del portal completo. Por eso
 *    el juego elegido abre su propio bloque y el resto queda rotulado como
 *    "portal completo", en vez de fingir que se recortó.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, BarController,
  LineElement, LineController, PointElement, Filler, Tooltip, Legend,
} from 'chart.js';
import {
  FaGift, FaSync, FaUsers, FaGamepad, FaTrophy, FaBolt,
  FaExclamationTriangle, FaClock, FaCalendarAlt, FaTable, FaChartLine,
} from 'react-icons/fa';
import { beneficiosApi } from '../../services/beneficiosService';
import { numFmt, Kpi, ErrorState, LoadingState } from '../../components/SocialApi/socialApiShared';
import '../../components/SocialApi/socialApi.css';
import './BeneficiosPage.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController,
  LineElement, LineController, PointElement, Filler, Tooltip, Legend);

const NAVY = '#073C5C';
const ORANGE = '#F68D2E';
const GREEN = '#0f9d63';
const GRAY = '#cbd5e1';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MESES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS_ES = {
  Monday: 'lunes', Tuesday: 'martes', Wednesday: 'miércoles', Thursday: 'jueves',
  Friday: 'viernes', Saturday: 'sábado', Sunday: 'domingo',
};

const GRANS = [
  { id: 'day', label: 'Diario' },
  { id: 'week', label: 'Semanal' },
  { id: 'month', label: 'Mensual' },
];
const RANGES = [
  { id: '90', label: '90 días' },
  { id: '180', label: '6 meses' },
  { id: '365', label: '12 meses' },
  { id: 'all', label: 'Todo' },
];

// ── Formato ─────────────────────────────────────────────────────────────────
const n = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : numFmt.format(Math.round(Number(v))));
const dec1 = (v) => (v == null ? '—' : Number(v).toFixed(1).replace('.', ','));
/** La API devuelve porcentajes como string ("54.72%") o como número. */
const parsePct = (v) => {
  if (v == null) return null;
  const x = parseFloat(String(v).replace('%', ''));
  return Number.isNaN(x) ? null : x;
};
const fmtPct = (v) => (v == null ? '—' : `${dec1(v)}%`);
const parseDay = (s) => {
  const p = String(s).slice(0, 10).split('-');
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
};
const fmtDay = (d) => `${d.getDate()} ${MESES[d.getMonth()]}`;

/** Semana ISO-8601 (lunes a domingo), igual que getRegistrationsByWeek. */
function isoWeek(dt) {
  const d = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + 3); // jueves de esa semana
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const week = 1 + Math.round(((d - jan4) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
  return { year: d.getFullYear(), week };
}
function weekStart(dt) {
  const d = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

/** Rellena los días sin actividad: sin esto la línea miente sobre el ritmo. */
function fillDays(days) {
  if (!days || !days.length) return [];
  const map = new Map(days.map((d) => [String(d.date).slice(0, 10), d]));
  const out = [];
  const cur = parseDay(days[0].date);
  const last = parseDay(days[days.length - 1].date);
  while (cur <= last) {
    const k = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    out.push(map.get(k) || { date: k, reg: 0, itx: 0 });
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Agrupa la serie diaria a día / semana ISO / mes. La API no da nada de esto. */
function rollup(days, gran) {
  if (gran === 'day') {
    return days.map((d) => {
      const dt = parseDay(d.date);
      return {
        key: d.date,
        label: fmtDay(dt),
        full: `${dt.getDate()} ${MESES_LARGO[dt.getMonth()]} ${dt.getFullYear()}`,
        reg: d.reg, itx: d.itx,
      };
    });
  }
  const buckets = new Map();
  days.forEach((d) => {
    const dt = parseDay(d.date);
    let key; let label; let full;
    if (gran === 'week') {
      const w = isoWeek(dt);
      key = `${w.year}-W${String(w.week).padStart(2, '0')}`;
      const ws = weekStart(dt);
      label = `S${w.week}`;
      full = `Semana ${w.week} · desde el ${fmtDay(ws)} ${ws.getFullYear()}`;
    } else {
      key = String(d.date).slice(0, 7);
      label = `${MESES[dt.getMonth()]} ${String(dt.getFullYear()).slice(2)}`;
      full = `${MESES_LARGO[dt.getMonth()]} ${dt.getFullYear()}`;
    }
    if (!buckets.has(key)) buckets.set(key, { key, label, full, reg: 0, itx: 0 });
    const b = buckets.get(key);
    b.reg += d.reg; b.itx += d.itx;
  });
  return Array.from(buckets.values());
}

// ── Opciones comunes de Chart.js ────────────────────────────────────────────
const baseOpts = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#0f172a',
      padding: 10,
      cornerRadius: 8,
      titleFont: { size: 12, weight: '700' },
      bodyFont: { size: 12 },
      ...(extra.tooltip || {}),
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkipPadding: 14 } },
    y: {
      beginAtZero: true,
      grid: { color: '#eef2f7' },
      border: { display: false },
      ticks: { font: { size: 10 }, precision: 0 },
    },
  },
  ...(extra.root || {}),
});

/** Leyenda propia: la de Chart.js no respeta el tamaño del resto de la app. */
function Legendita({ items }) {
  return (
    <div className="ben-legend">
      {items.map((it) => (
        <span key={it.label}>
          <i style={{ background: it.color, borderRadius: it.line ? 2 : 3, height: it.line ? 3 : 10 }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

/** Barra del embudo. La etiqueta se mete dentro solo si el relleno da el ancho. */
function FunnelBar({ label, value, width, color, inside, sub }) {
  return (
    <div className="ben-funnel-row">
      <div className="ben-funnel-track">
        <div className="ben-funnel-fill" style={{ width: `${Math.max(width, 2)}%`, background: color }}>
          {inside && <span className="ben-funnel-in">{label} {n(value)}</span>}
        </div>
        {!inside && <span className="ben-funnel-out">{label} {n(value)}</span>}
      </div>
      {sub && <div className="ben-funnel-sub">{sub}</div>}
    </div>
  );
}

/**
 * Pestaña Juegos: lista maestra a la izquierda, detalle del juego a la derecha.
 * Todo lo que se ve aquí SÍ es por juego; lo del portal vive en la otra
 * pestaña, así que no hace falta advertir en cada tarjeta qué se filtra.
 */
function GamesPanel({ games, sel, stats, onSelect, timesPartial }) {
  if (!games.length) {
    return <div className="soc-empty">Ningún juego registra participaciones.</div>;
  }
  const pos = (v) => (stats.maxTime ? `${Math.min((v / stats.maxTime) * 100, 100)}%` : '0%');
  const share = stats.totalItx ? (sel.interactions / stats.totalItx) * 100 : null;
  const rep = sel.users ? sel.interactions / sel.users : null;
  const hasTimes = sel.avg != null;
  const vsAvg = hasTimes && stats.avgOfAvg ? ((sel.avg - stats.avgOfAvg) / stats.avgOfAvg) * 100 : null;

  return (
    <div className="ben-games">
      <div className="soc-chart-card ben-glist">
        <div className="soc-chart-head">
          <span className="soc-chart-title"><FaGamepad /> Dinámicas</span>
          <span className="soc-chart-tag">por interacciones</span>
        </div>
        <div className="ben-glist-items">
          {games.map((g) => (
            <button key={g.id} type="button"
              className={`ben-gitem ${g.id === sel.id ? 'active' : ''}`}
              onClick={() => onSelect(g.id)}>
              <span className="ben-gitem-rank">{g.rank}</span>
              <span className="ben-gitem-body">
                <span className="ben-gitem-top">
                  <span className="ben-gitem-name">{g.title}</span>
                  <span className="ben-gitem-val">{n(g.interactions)}</span>
                </span>
                <span className="ben-gitem-bar">
                  <i style={{ width: `${stats.maxItx ? (g.interactions / stats.maxItx) * 100 : 0}%` }} />
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="soc-chart-card ben-gdetail">
        <div className="ben-gd-head">
          <span className="ben-gd-rank">#{sel.rank}</span>
          <div>
            <h2 className="ben-gd-title">{sel.title}</h2>
            <p className="ben-gd-sub">
              {sel.rank} de {games.length} dinámicas · {fmtPct(share)} de todas las interacciones
            </p>
          </div>
        </div>

        <div className="ben-gd-stats">
          <div><span>Interacciones</span><strong>{n(sel.interactions)}</strong></div>
          <div><span>Usuarios únicos</span><strong>{n(sel.users)}</strong></div>
          <div>
            <span>Por usuario</span>
            <strong>{rep == null ? '—' : rep.toFixed(2).replace('.', ',')}</strong>
          </div>
        </div>

        <div className="ben-gd-block">
          <span className="ben-gd-label"><FaClock /> Tiempo de resolución</span>
          {hasTimes ? (
            <>
              <div className="ben-range">
                <div className="ben-range-track" />
                {sel.min != null && sel.max != null && !timesPartial && (
                  <div className="ben-range-span"
                    style={{ left: pos(sel.min), width: `calc(${pos(sel.max)} - ${pos(sel.min)})` }} />
                )}
                {stats.avgOfAvg != null && (
                  <div className="ben-range-ref" style={{ left: pos(stats.avgOfAvg) }}
                    title={`Promedio de todos los juegos: ${dec1(stats.avgOfAvg)} s`} />
                )}
                <div className="ben-range-dot" style={{ left: pos(sel.avg) }} />
              </div>
              <div className="ben-range-legend">
                <div><span>Más rápido</span><strong>{sel.min != null ? `${dec1(sel.min)} s` : '—'}</strong></div>
                <div className="mid"><span>Promedio</span><strong>{dec1(sel.avg)} s</strong></div>
                <div className="end">
                  <span>Más lento</span>
                  <strong>{!timesPartial && sel.max != null ? `${dec1(sel.max)} s` : '—'}</strong>
                </div>
              </div>
              {vsAvg != null && (
                <p className="ben-note">
                  <i className="ben-ref-key" /> El promedio de este juego es{' '}
                  {fmtPct(Math.abs(vsAvg))} {vsAvg >= 0 ? 'más lento' : 'más rápido'} que el promedio de
                  todas las dinámicas ({dec1(stats.avgOfAvg)} s).
                </p>
              )}
            </>
          ) : (
            <p className="ben-note">Esta dinámica no registra tiempos de inicio y fin válidos.</p>
          )}
        </div>

        <p className="ben-note ben-gd-foot">
          Por juego, la API solo reporta interacciones, usuarios y tiempos, y no identifica a los
          jugadores. Registros, premios y horarios están en la pestaña Portal.
        </p>
      </div>
    </div>
  );
}

export default function BeneficiosPage() {
  const [status, setStatus] = useState(null);
  const [tenant, setTenant] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gran, setGran] = useState('month');
  const [range, setRange] = useState('all');
  const [showTable, setShowTable] = useState(false);
  const [gameId, setGameId] = useState('');
  const [view, setView] = useState('portal');

  // 1) ¿hay token configurado? 2) cargar el tenant por defecto
  useEffect(() => {
    let alive = true;
    beneficiosApi.status().then((s) => {
      if (!alive) return;
      setStatus(s);
      setTenant((t) => t || s.default_tenant || '');
    }).catch((e) => {
      if (alive) { setError(e.message); setLoading(false); }
    });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async (opts = {}) => {
    if (!tenant) return;
    setLoading(true); setError(null);
    try {
      const d = await beneficiosApi.overview(tenant, opts);
      setData(d);
    } catch (e) {
      setError(e.code === 'not_configured'
        ? 'Falta configurar BENEFICIOS_API_TOKEN en el backend.'
        : e.message);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => { if (tenant) load(); }, [tenant, load]);

  // El lugar se calcula aquí (la lista ya viene ordenada por interacciones)
  // para no depender de que el backend mande `rank`.
  const games = useMemo(
    () => (data?.games || []).map((g, i) => ({ ...g, rank: i + 1 })),
    [data],
  );
  // Sin elección (o si el juego no existe en este tenant) se muestra el primero.
  const sel = useMemo(
    () => games.find((g) => g.id === gameId) || games[0] || null,
    [games, gameId],
  );
  const gameStats = useMemo(() => {
    const totalItx = games.reduce((a, g) => a + (g.interactions || 0), 0);
    const timed = games.filter((g) => g.avg != null);
    const avgOfAvg = timed.length ? timed.reduce((a, g) => a + g.avg, 0) / timed.length : null;
    const maxItx = Math.max(0, ...games.map((g) => g.interactions || 0));
    const maxTime = Math.max(0, ...games.map((g) => g.max ?? g.avg ?? 0));
    return { totalItx, avgOfAvg, maxItx, maxTime };
  }, [games]);

  // ── Derivados del histórico ───────────────────────────────────────────────
  const filled = useMemo(() => fillDays(data?.days || []), [data]);

  const sliced = useMemo(() => {
    if (range === 'all' || !filled.length) return filled;
    const last = parseDay(filled[filled.length - 1].date);
    const from = new Date(last);
    from.setDate(from.getDate() - Number(range) + 1);
    return filled.filter((d) => parseDay(d.date) >= from);
  }, [filled, range]);

  const rows = useMemo(() => rollup(sliced, gran), [sliced, gran]);

  const histData = useMemo(() => ({
    labels: rows.map((r) => r.label),
    datasets: [
      {
        label: 'Registros',
        data: rows.map((r) => r.reg),
        borderColor: NAVY,
        backgroundColor: 'rgba(7,60,92,.10)',
        borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, tension: .25, fill: true,
      },
      {
        label: 'Interacciones',
        data: rows.map((r) => r.itx),
        borderColor: ORANGE,
        backgroundColor: 'rgba(246,141,46,.10)',
        borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, tension: .25, fill: true,
      },
    ],
  }), [rows]);

  const histOpts = useMemo(() => baseOpts({
    tooltip: {
      callbacks: {
        title: (items) => rows[items[0].dataIndex]?.full || '',
        label: (it) => ` ${it.dataset.label}: ${n(it.parsed.y)}`,
      },
    },
  }), [rows]);

  const cumData = useMemo(() => {
    let run = 0;
    const pts = filled.map((d) => { run += d.reg; return run; });
    return {
      labels: filled.map((d) => fmtDay(parseDay(d.date))),
      datasets: [{
        label: 'Usuarios acumulados',
        data: pts,
        borderColor: NAVY,
        backgroundColor: 'rgba(7,60,92,.10)',
        borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, tension: .2, fill: true,
      }],
    };
  }, [filled]);

  const hoursData = useMemo(() => ({
    labels: (data?.hours || []).map((h) => String(h.hour).padStart(2, '0')),
    datasets: [
      { label: 'Registros', data: (data?.hours || []).map((h) => h.reg), backgroundColor: NAVY, borderRadius: 4, maxBarThickness: 14 },
      { label: 'Interacciones', data: (data?.hours || []).map((h) => h.itx), backgroundColor: ORANGE, borderRadius: 4, maxBarThickness: 14 },
    ],
  }), [data]);

  // ── Estados de página ─────────────────────────────────────────────────────
  if (status && !status.configured) {
    return (
      <main className="soc-page">
        <div className="soc-state">
          <FaGift className="big" />
          <h3>Beneficios Cantabria no está conectado</h3>
          <p>
            Un administrador debe cargar el token de la API de BI del portal de
            promociones en la variable <code>BENEFICIOS_API_TOKEN</code> del backend.
          </p>
        </div>
      </main>
    );
  }
  if (loading && !data) return <main className="soc-page"><LoadingState label="Cargando el portal de beneficios…" /></main>;
  if (error && !data) return <ErrorState message={error} onRetry={() => load({ refresh: true })} />;
  if (!data) return null;

  const s = data.summary || {};
  const growth = data.growth || {};
  const gr = growth.growth_rate == null ? null : parseFloat(growth.growth_rate);
  const partRate = parsePct(s.participation_rate);
  const awardRate = parsePct(s.award_rate);
  const awards = Number(s.total_awards_won || 0);
  const without = Number(s.participations_without_award || 0);
  const totalPart = awards + without;
  const reg = Number(s.registered_users || 0);
  const part = Number(s.participating_users || 0);
  // Puede pasar de 100 % cuando el origen no filtra las interacciones por tenant
  // (ver el aviso que manda el backend). Se acota para no reventar la tarjeta.
  const partRaw = reg ? (part / reg) * 100 : 0;
  const partWidth = Math.min(partRaw, 100);
  const partBroken = partRaw > 100;
  const peakR = data.peak_registration_hour;
  const peakI = data.peak_interaction_hour;
  const wd = data.most_active_weekday;
  const rangeLabel = sliced.length
    ? `${fmtDay(parseDay(sliced[0].date))} ${parseDay(sliced[0].date).getFullYear()} – ${fmtDay(parseDay(sliced[sliced.length - 1].date))} ${parseDay(sliced[sliced.length - 1].date).getFullYear()}`
    : 'sin datos';

  return (
    <main className="soc-page ben-page">
      <header className="soc-header">
        <div className="soc-header-left">
          <span className="soc-title-icon" style={{ background: NAVY }}><FaGift /></span>
          <div>
            <h1 className="soc-title">Beneficios Cantabria</h1>
            <p className="soc-subtitle">
              Participación, juegos y premios del portal de promociones.
              {data.cached && ' Datos en caché (se refrescan cada 5 min).'}
            </p>
          </div>
        </div>
        <div className="soc-header-actions">
          {(status?.tenants || []).length > 1 && (
            <select className="ben-tenant" value={tenant} onChange={(e) => setTenant(e.target.value)}>
              {status.tenants.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <button type="button" className="soc-btn" onClick={() => load({ refresh: true })} disabled={loading}>
            <FaSync className={loading ? 'soc-spin' : ''} /> Actualizar
          </button>
        </div>
      </header>

      <nav className="ben-tabs" role="tablist">
        {[
          { id: 'portal', label: 'Portal', icon: <FaGift /> },
          { id: 'juegos', label: `Juegos · ${games.length}`, icon: <FaGamepad /> },
        ].map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={view === t.id}
            className={`ben-tab ${view === t.id ? 'active' : ''}`} onClick={() => setView(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </nav>

      {(data.warnings || []).length > 0 && (
        <div className="soc-warn">
          <FaExclamationTriangle />
          <div>
            <strong>Avisos de la API de origen:</strong>
            <ul>{data.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        </div>
      )}

      {view === 'juegos' && (
        <GamesPanel games={games} sel={sel} stats={gameStats} onSelect={setGameId}
          timesPartial={data.times_partial} />
      )}

      {view === 'portal' && (<>
      {/* El malentendido caro: los KPIs NO se filtran. Se dice antes de mostrarlos. */}
      <div className="soc-warn ben-scope">
        <FaExclamationTriangle />
        <div>
          <strong>Los indicadores son acumulados de todo el periodo.</strong>{' '}
          La API de beneficios no acepta filtros de fecha en ningún endpoint, así que
          el rango de abajo solo recorta las series históricas, nunca estas tarjetas.
        </div>
      </div>

      {/* ── Resumen general (getSummary + getUserGrowth) ── */}
      <div className="soc-kpis ben-kpis">
        <Kpi
          icon={<FaUsers />} accent={NAVY} label="Usuarios registrados" value={n(reg)}
          sub={gr == null
            ? `${n(data.new_users_month)} nuevos este mes`
            : `${gr >= 0 ? '▲' : '▼'} ${fmtPct(Math.abs(gr))} vs mes anterior`}
        />
        <Kpi
          icon={<FaBolt />} accent={ORANGE} label="Participantes" value={n(part)}
          sub={`${fmtPct(partRate)} de los registrados`}
        />
        <Kpi
          icon={<FaGamepad />} accent={ORANGE} label="Interacciones" value={n(s.total_interactions)}
          sub={`${dec1(s.average_interactions_per_user)} por usuario participante`}
        />
        <Kpi
          icon={<FaTrophy />} accent={GREEN} label="Premios ganados" value={n(awards)}
          sub={`${fmtPct(awardRate)} de las participaciones`}
        />
        <Kpi
          icon={<FaGamepad />} accent={NAVY} label="Juegos utilizados" value={n(s.total_used_games)}
          sub={data.most_used_game ? `Más usado: ${data.most_used_game.model_title || data.most_used_game.title}` : 'Con al menos una participación'}
        />
      </div>

      {/* ── Histórico: diario / semanal / mensual ── */}
      <div className="ben-hist-head">
        <div className="ben-hist-ctrl">
          <span className="ben-ctrl-lab">Vista</span>
          <div className="soc-datefilter">
            {GRANS.map((g) => (
              <button key={g.id} type="button"
                className={`soc-chip ${gran === g.id ? 'active' : ''}`}
                onClick={() => setGran(g.id)}>{g.label}</button>
            ))}
          </div>
          <span className="ben-ctrl-lab">Rango</span>
          <div className="soc-datefilter">
            {RANGES.map((r) => (
              <button key={r.id} type="button"
                className={`soc-chip ${range === r.id ? 'active' : ''}`}
                onClick={() => setRange(r.id)}>{r.label}</button>
            ))}
          </div>
        </div>
        <button type="button" className="soc-btn" onClick={() => setShowTable((v) => !v)}>
          {showTable ? <><FaChartLine /> Ver gráfica</> : <><FaTable /> Ver tabla</>}
        </button>
      </div>

      <div className="soc-grid">
        <div className="soc-chart-card col-12">
          <div className="soc-chart-head">
            <span className="soc-chart-title">
              <FaChartLine />
              {gran === 'day' ? 'Volumen por día' : gran === 'week' ? 'Volumen por semana ISO' : 'Volumen por mes'}
            </span>
            <span className="soc-chart-tag">{rangeLabel} · {rows.length} {gran === 'day' ? 'días' : gran === 'week' ? 'semanas' : 'meses'}</span>
          </div>

          {showTable ? (
            <div className="ben-table-wrap">
              <table className="ben-table">
                <thead>
                  <tr><th>Periodo</th><th>Registros</th><th>Interacciones</th><th>Interacciones por registro</th></tr>
                </thead>
                <tbody>
                  {rows.slice().reverse().map((r) => (
                    <tr key={r.key}>
                      <td>{r.full}</td>
                      <td>{n(r.reg)}</td>
                      <td>{n(r.itx)}</td>
                      <td>{r.reg ? (r.itx / r.reg).toFixed(2).replace('.', ',') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="soc-chart-box tall">
                {rows.length ? <Line data={histData} options={histOpts} /> : <div className="soc-empty">Sin datos en este rango.</div>}
              </div>
              <Legendita items={[
                { label: 'Registros de usuarios', color: NAVY, line: true },
                { label: 'Interacciones', color: ORANGE, line: true },
              ]} />
            </>
          )}
        </div>

        <div className="soc-chart-card col-8">
          <div className="soc-chart-head">
            <span className="soc-chart-title"><FaUsers /> Base acumulada de usuarios</span>
            <span className="soc-chart-tag">suma corrida de los registros diarios</span>
          </div>
          <div className="soc-chart-box">
            {filled.length ? <Line data={cumData} options={baseOpts()} /> : <div className="soc-empty">Sin serie diaria.</div>}
          </div>
        </div>

        <div className="soc-chart-card col-4">
          <div className="soc-chart-head">
            <span className="soc-chart-title"><FaTrophy /> Del registro al premio</span>
          </div>
          <div className="ben-funnel">
            <span className="ben-funnel-cap">Usuarios</span>
            <FunnelBar label="Registrados" value={reg} width={100} color="#9ec5f4" inside />
            <FunnelBar
              label="Participantes" value={part} width={partWidth} color={NAVY}
              inside={partWidth > 42}
              sub={partBroken
                ? `${fmtPct(partRate)} — dato inconsistente del origen`
                : `${fmtPct(partRate)} de los registrados`}
            />
            <span className="ben-funnel-cap">Participaciones</span>
            <div className="ben-split">
              <div style={{ width: `${totalPart ? (awards / totalPart) * 100 : 0}%`, background: GREEN }} />
              <div style={{ width: `${totalPart ? (without / totalPart) * 100 : 100}%`, background: GRAY }} />
            </div>
            <div className="ben-split-legend">
              <span><i style={{ background: GREEN }} />{n(awards)} con premio · {fmtPct(awardRate)}</span>
              <span><i style={{ background: GRAY }} />{n(without)} sin premio</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Ritmo del día ── */}
      <div className="soc-grid">
        <div className="soc-chart-card col-12">
          <div className="soc-chart-head">
            <span className="soc-chart-title"><FaClock /> Registros e interacciones por hora</span>
            <span className="soc-chart-tag">acumulado de todo el periodo</span>
          </div>
          <div className="soc-chart-box">
            <Bar data={hoursData} options={baseOpts()} />
          </div>
          <Legendita items={[
            { label: 'Registros', color: NAVY },
            { label: 'Interacciones', color: ORANGE },
          ]} />
        </div>
      </div>

      <div className="soc-kpis ben-kpis-4">
        <Kpi icon={<FaClock />} accent={NAVY} label="Hora pico de registro"
          value={peakR ? `${String(peakR.hour).padStart(2, '0')}:00` : '—'}
          sub={peakR ? `${n(peakR.total_registrations)} registros en esa franja` : ''} />
        <Kpi icon={<FaBolt />} accent={ORANGE} label="Hora pico de interacción"
          value={peakI ? `${String(peakI.hour).padStart(2, '0')}:00` : '—'}
          sub={peakI ? `${n(peakI.total_interactions)} interacciones en esa franja` : ''} />
        <Kpi icon={<FaCalendarAlt />} accent={GREEN} label="Día más activo"
          value={wd ? (DIAS_ES[wd.weekday] || wd.weekday) : '—'}
          sub={wd ? `${n(wd.total_interactions)} interacciones · se mide por interacciones` : ''} />
        <Kpi icon={<FaClock />} accent={NAVY} label="Hora promedio de registro"
          value={data.average_registration_time ? String(data.average_registration_time).slice(0, 5) : '—'}
          sub="Promedio de la hora del alta" />
      </div>

      </>)}

      {(data.errors || []).length > 0 && (
        <div className="soc-warn">
          <FaExclamationTriangle />
          <div>
            <strong>{data.errors.length} endpoint(s) no respondieron:</strong>{' '}
            {data.errors.join(' · ')}
          </div>
        </div>
      )}
    </main>
  );
}
